import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { copyToClipboard } from './useMediscribe';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * useDocumentExport — Handles all export, copy, share, and letter placeholder logic.
 * Extracted from App.js to reduce monolith.
 */
const useDocumentExport = ({
  setCopiedField, setLastShareUrl,
  providerSignatureBlock, letterheadLine1, letterheadLine2, letterheadAddress,
  soldierPhone, setSoldierPhone, soldierRank, soldierFirstName, soldierLastName,
  soldierDob, soldierUnit, letterType, mtfName, customMtf,
  showHistory, loadHistory,
}) => {
  const getEffectiveMtf = useCallback(() => mtfName === 'custom' ? customMtf : mtfName, [mtfName, customMtf]);

  const handleCopy = useCallback(async (text, field) => {
    if (!text) { toast.error('Nothing to copy'); return; }
    try {
      if (await copyToClipboard(text)) { setCopiedField(field); toast.success('Copied! Paste into Word/Docs.'); setTimeout(() => setCopiedField(null), 2000); }
      else toast.error('Copy failed - try selecting text manually');
    } catch (e) { toast.error('Failed to copy'); }
  }, [setCopiedField]);

  const buildSignatureBlock = useCallback(() => providerSignatureBlock
    ? `\n\n\n\n${providerSignatureBlock}`
    : `\n\n\n\n                                        ________________________________\n                                        (NAME IN ALL CAPS)\n                                        (Rank, Branch/Corps)\n                                        (Duty Title)\n                                        (Organization/MTF)`,
  [providerSignatureBlock]);

  const downloadBlob = useCallback((data, filename, ext) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a'); link.href = url; link.setAttribute('download', `${filename}.${ext}`);
    document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
  }, []);

  const exportToWord = useCallback(async (content, docType, filename) => {
    try {
      const exportContent = docType === 'letter' ? content + buildSignatureBlock() : content;
      const r = await axios.post(`${API}/export/docx`, { content: exportContent, doc_type: docType, filename, letterhead: { line1: letterheadLine1, line2: letterheadLine2, address: letterheadAddress } }, { responseType: 'arraybuffer' });
      downloadBlob(new Blob([r.data]), filename, 'docx'); toast.success('Word document downloaded!');
    } catch (e) { toast.error('Failed to export Word document'); }
  }, [buildSignatureBlock, downloadBlob, letterheadLine1, letterheadLine2, letterheadAddress]);

  const exportToPDF = useCallback(async (content, docType, filename) => {
    try {
      const exportContent = docType === 'letter' ? content + buildSignatureBlock() : content;
      const r = await axios.post(`${API}/export/pdf`, { content: exportContent, doc_type: docType, filename, letterhead: { line1: letterheadLine1, line2: letterheadLine2, address: letterheadAddress } }, { responseType: 'arraybuffer' });
      downloadBlob(new Blob([r.data]), filename, 'pdf'); toast.success('PDF downloaded!');
    } catch (e) { toast.error('Failed to export PDF'); }
  }, [buildSignatureBlock, downloadBlob, letterheadLine1, letterheadLine2, letterheadAddress]);

  const shareLetter = useCallback(async (content, historyItemId = null) => {
    let phoneToUse = soldierPhone;
    if (!phoneToUse) { const p = window.prompt('Enter phone number(s):'); if (!p) { toast.error('Phone number required'); return; } phoneToUse = p; setSoldierPhone(p); }
    const phoneNumbers = phoneToUse.split(/[,;]/).map(p => p.replace(/\D/g, '')).filter(p => p.length >= 10);
    if (phoneNumbers.length === 0) { toast.error('Please enter valid phone number(s)'); return; }
    const fullName = `${soldierRank} ${soldierFirstName} ${soldierLastName}`.trim();
    try {
      const r = await axios.post(`${API}/share/sms`, { phone: phoneNumbers.join(','), soldier_name: fullName || 'Soldier', letter_content: content, letter_type: letterType || 'Medical Letter', message: '', history_id: historyItemId });
      if (r.data.success && r.data.share_url) {
        setLastShareUrl(r.data.share_url); await copyToClipboard(r.data.share_url);
        const smsMsg = r.data.sms_message || `DRAFT Medical Letter for ${fullName || 'review'}. View here: ${r.data.share_url} (expires in 72hrs)`;
        if (r.data.sms_sent) toast.success(`SMS Sent! Link copied: ${r.data.share_url}`, { duration: 6000 });
        else { phoneNumbers.forEach((ph, i) => setTimeout(() => window.open(`sms:${ph}?body=${encodeURIComponent(smsMsg)}`, '_blank'), i * 300)); toast.info('Share link created & copied!', { duration: 6000 }); }
        if (showHistory) loadHistory();
      }
    } catch (e) { toast.error('Failed to create share link.'); }
  }, [soldierPhone, setSoldierPhone, soldierRank, soldierFirstName, soldierLastName, letterType, setLastShareUrl, showHistory, loadHistory]);

  const fillLetterPlaceholders = useCallback((letter) => {
    if (!letter) return letter;
    let f = letter;
    const fullName = `${soldierRank} ${soldierFirstName} ${soldierLastName}`.trim();
    const effectiveMtf = getEffectiveMtf();
    if (fullName && fullName.length > 2) f = f.replace(/\[(?:Rank\/Name|Rank Name|RANK\/NAME|Rank, Name|Employee Name|Patient Name)\]/gi, fullName);
    if (soldierRank) f = f.replace(/\[Rank\]/gi, soldierRank);
    if (soldierFirstName) f = f.replace(/\[First(?:\s+Name)?\]/gi, soldierFirstName);
    if (soldierLastName) f = f.replace(/\[Last(?:\s+Name)?\]/gi, soldierLastName);
    if (soldierDob) f = f.replace(/\[(?:DOB|Date of Birth)\]/gi, soldierDob);
    if (soldierUnit) f = f.replace(/\[(?:Unit|Unit Name)\]/gi, soldierUnit);
    if (effectiveMtf) f = f.replace(/\[(?:MTF(?:\/Clinic)?\s*(?:Name)?|Clinic(?:\/Department)?|CLINIC\/PROVIDER LETTERHEAD|UNIT\/CLINIC LETTERHEAD|[Ll]ocation)\]/gi, effectiveMtf);
    if (providerSignatureBlock) { const name = providerSignatureBlock.split(',')[0] || providerSignatureBlock; f = f.replace(/\[Provider Name\], \[Credentials\](?:, \[Clinic\/MTF\])?/gi, providerSignatureBlock).replace(/\[Provider Name, Rank, Corps\]/gi, providerSignatureBlock).replace(/\[Provider Name\]/gi, name).replace(/\[Your Name\](?:, \[Credentials\])?/gi, providerSignatureBlock); }
    return f;
  }, [soldierRank, soldierFirstName, soldierLastName, soldierDob, soldierUnit, getEffectiveMtf, providerSignatureBlock]);

  const getFilledLetter = useCallback((content) => fillLetterPlaceholders(content), [fillLetterPlaceholders]);

  const copyAllDocuments = useCallback(async (result, noteTitle, manualIcd10, primaryIcd10Codes, signatureBlock) => {
    let allContent = [];
    const allIcd10 = [];
    if (manualIcd10) manualIcd10.split(',').forEach(c => { const t = c.trim().toUpperCase(); if (t && !allIcd10.includes(t)) allIcd10.push(t); });
    if (result?.icd10_suggestions?.length > 0) {
      const primary = result.icd10_suggestions.filter(s => primaryIcd10Codes.includes(s.code));
      primary.forEach(i => { if (!allIcd10.includes(i.code)) allIcd10.push(i.code); });
      result.icd10_suggestions.forEach(i => { if (!allIcd10.includes(i.code)) allIcd10.push(i.code); });
    }
    if (allIcd10.length > 0) allContent.push(`ICD-10 CODES: ${allIcd10.join(', ')}`);
    if (result.referrals?.length > 0) result.referrals.forEach(r => allContent.push(`REFERRAL: ${r.specialty}\n${r.indication}`));
    else if (result.indication) allContent.push(`REFERRAL\n${result.indication}`);
    if (result.imaging_orders?.length > 0) result.imaging_orders.forEach(i => allContent.push(`IMAGING: ${i.imaging_type}${i.body_part ? ` - ${i.body_part}` : ''}\n${i.indication}`));
    if (result.clinical_note) allContent.push(`${noteTitle || 'CLINICAL NOTE'}\n${result.clinical_note}`);
    if (result.admin_letter) allContent.push(`ADMINISTRATIVE LETTER\n${result.admin_letter}`);
    if (result.limitations) allContent.push(`FUNCTIONAL LIMITATIONS\n${result.limitations}`);
    if (signatureBlock) allContent.push(`---\n${signatureBlock}`);
    const success = await copyToClipboard(allContent.join('\n\n'));
    if (success) { setCopiedField('all-docs'); toast.success('All documents copied!'); setTimeout(() => setCopiedField(null), 2000); }
    else toast.error('Copy failed');
  }, [setCopiedField]);

  // Feb 26, 2026 — Note-only and Orders-only copies. Surface in the top
  // toolbar so the provider can paste JUST the SOAP note OR JUST the orders
  // block without manually highlighting. Pharmacy/scheduling staff often
  // need only the orders; specialists often need only the note.
  const copyClinicalNoteOnly = useCallback(async (result, noteTitle, signatureBlock) => {
    if (!result?.clinical_note) { toast.error('No clinical note to copy'); return; }
    let parts = [`${noteTitle || 'CLINICAL NOTE'}\n${result.clinical_note}`];
    if (signatureBlock) parts.push(`---\n${signatureBlock}`);
    const ok = await copyToClipboard(parts.join('\n\n'));
    if (ok) { setCopiedField('note-only'); toast.success('Clinical note copied!'); setTimeout(() => setCopiedField(null), 2000); }
    else toast.error('Copy failed');
  }, [setCopiedField]);

  const copyOrdersOnly = useCallback(async (result, manualIcd10, primaryIcd10Codes) => {
    if (!result) { toast.error('Nothing to copy'); return; }
    const parts = [];
    const allIcd10 = [];
    if (manualIcd10) manualIcd10.split(',').forEach(c => { const t = c.trim().toUpperCase(); if (t && !allIcd10.includes(t)) allIcd10.push(t); });
    if (result.icd10_suggestions?.length > 0) {
      const primary = result.icd10_suggestions.filter(s => (primaryIcd10Codes || []).includes(s.code));
      primary.forEach(i => { if (!allIcd10.includes(i.code)) allIcd10.push(i.code); });
      result.icd10_suggestions.forEach(i => { if (!allIcd10.includes(i.code)) allIcd10.push(i.code); });
    }
    if (allIcd10.length > 0) parts.push(`ICD-10 CODES: ${allIcd10.join(', ')}`);
    if (result.referrals?.length > 0) result.referrals.forEach(r => parts.push(`REFERRAL: ${r.specialty}\n${r.indication}`));
    if (result.imaging_orders?.length > 0) result.imaging_orders.forEach(i => parts.push(`IMAGING: ${i.imaging_type}${i.body_part ? ` - ${i.body_part}` : ''}\n${i.indication}`));
    if (parts.length === 0) { toast.error('No orders to copy'); return; }
    const ok = await copyToClipboard(parts.join('\n\n'));
    if (ok) { setCopiedField('orders-only'); toast.success('Orders copied!'); setTimeout(() => setCopiedField(null), 2000); }
    else toast.error('Copy failed');
  }, [setCopiedField]);

  return {
    handleCopy, exportToWord, exportToPDF, shareLetter,
    fillLetterPlaceholders, getFilledLetter, copyAllDocuments,
    copyClinicalNoteOnly, copyOrdersOnly,
    getEffectiveMtf,
  };
};

export default useDocumentExport;
